import React, { useEffect, useRef } from 'react'

type TranscribedSegment = {
  timestamp: string
  speaker: string
  text: string
  highlighted?: boolean
}

type TranscribedAudioPanelProps = {
  segments: TranscribedSegment[]
}

const NEAR_BOTTOM_THRESHOLD_PX = 48

const TranscribedAudioPanel: React.FC<TranscribedAudioPanelProps> = ({
  segments,
}) => {
  const bodyRef = useRef<HTMLDivElement | null>(null)
  const stickToBottomRef = useRef(true)

  const handleBodyScroll = (): void => {
    const el = bodyRef.current
    if (!el) return
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight
    stickToBottomRef.current = distanceFromBottom <= NEAR_BOTTOM_THRESHOLD_PX
  }

  useEffect(() => {
    const el = bodyRef.current
    if (!el || !stickToBottomRef.current) return
    el.scrollTop = el.scrollHeight
  }, [segments])

  return (
    <aside className="transcribed-audio-container">
      <div className="transcribed-audio-inner">
        <div className="transcribed-audio-header">
          <span className="transcribed-audio-header-title">
            Transcribed audio
          </span>
          <button
            type="button"
            className="transcribed-audio-header-action"
            aria-label="Download transcription"
          >
            <i className="mgc mgc_download_line" />
          </button>
        </div>
        <div
          className="transcribed-audio-body"
          ref={bodyRef}
          onScroll={handleBodyScroll}
        >
          <div className="transcribed-audio-body-inner">
            {segments.length === 0 ? (
              <div className="transcribed-audio-body-content">
                <div className="transcribed-audio-text">
                  No transcription available yet.
                </div>
              </div>
            ) : (
              segments.map((segment) => (
                <div
                  key={`${segment.timestamp}-${segment.text}`}
                  className="transcribed-audio-body-content"
                >
                  <div
                    className={`transcribed-audio-speaker ${
                      segment.highlighted
                        ? 'transcribed-audio-speaker-highlight'
                        : ''
                    }`}
                  >
                    <span className="transcribed-audio-speaker-time">
                      {segment.timestamp}
                    </span>
                    <span className="transcribed-audio-speaker-name">
                      {segment.speaker}
                    </span>
                  </div>
                  <div className="transcribed-audio-text">{segment.text}</div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </aside>
  )
}

export type { TranscribedSegment }
export default TranscribedAudioPanel
